/**
 * Global keyboard shortcuts.
 *
 * Mounted once at the root via useGlobalShortcuts(). Bails out whenever the
 * keypress originates inside an editable field (input/textarea/select or any
 * contenteditable like the Milkdown editor) so typing letters in a note never
 * triggers a shortcut.
 *
 * The "new note" action piggybacks on the existing FAB handler in NotesGrid —
 * we dispatch a `keep:new-note` window event and the mounted grid creates +
 * opens a fresh note. If the user isn't on a grid route we navigate home
 * first, then fire the event on the next tick so the grid has time to mount.
 */

import { useEffect } from "react";
import { useNavigate, useRouterState } from "@tanstack/react-router";

type Navigate = ReturnType<typeof useNavigate>;

/** Items rendered by the help modal. The first key listed is the primary; any
 *  others are aliases shown side-by-side. */
export const SHORTCUTS = [
  { keys: ["c"], description: "New note" },
  { keys: ["/", "⌘ K"], description: "Search" },
  { keys: ["?"], description: "Show this help" },
  { keys: ["Esc"], description: "Close dialog" },
] as const;

/** Custom window events the shortcuts dispatch. */
export const SHORTCUT_EVENTS = {
  newNote: "keep:new-note",
  showHelp: "keep:show-help",
} as const;

function isEditable(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

// Routes where an inline-created note actually shows up in the list. Home and
// folder views qualify; Archive/Reminders do NOT — a brand-new note isn't
// archived and has no reminder, so it would be created then instantly vanish
// from the filtered view. On those, fall through to navigate-home-then-create.
const GRID_ROUTES = ["/"];
function isGridRoute(path: string): boolean {
  return GRID_ROUTES.includes(path) || path.startsWith("/folders/");
}

/** Trigger the "new note" flow. If we're on a route with a NotesGrid mounted,
 *  fire the event directly — the grid creates the note and opens the editor.
 *  Otherwise navigate home first and fire after the grid has mounted. Used by
 *  both the `c` shortcut and the global FAB. */
export function triggerNewNote(navigate: Navigate, path: string): void {
  if (isGridRoute(path)) {
    window.dispatchEvent(new CustomEvent(SHORTCUT_EVENTS.newNote));
  } else {
    navigate({ to: "/" });
    setTimeout(() => window.dispatchEvent(new CustomEvent(SHORTCUT_EVENTS.newNote)), 50);
  }
}

export function useGlobalShortcuts() {
  const navigate = useNavigate();
  const path = useRouterState({ select: (s) => s.location.pathname });

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (isEditable(e.target)) return;

      // Cmd/Ctrl + K → search. Handled before the no-modifier branch so it
      // doesn't get swallowed by the bare-key guard below.
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        if (path !== "/search") navigate({ to: "/search" });
        return;
      }

      // Below: bare-key shortcuts. Shift is allowed (needed for "?"), other
      // modifiers are not.
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      const k = e.key;
      if (k === "c" || k === "C" || k === "n" || k === "N") {
        e.preventDefault();
        triggerNewNote(navigate, path);
      } else if (k === "/") {
        e.preventDefault();
        if (path !== "/search") navigate({ to: "/search" });
      } else if (k === "?") {
        e.preventDefault();
        window.dispatchEvent(new CustomEvent(SHORTCUT_EVENTS.showHelp));
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate, path]);
}
